import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Text from "@mui/material/Typography";
import { Component, ErrorInfo, ReactNode } from "react";

type FallbackRender = (error: Error, info: ErrorInfo | undefined) => ReactNode;

interface Props {
    children: ReactNode;
    fallback?: ReactNode | FallbackRender;
    onError?: (error: Error, info: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
    info?: ErrorInfo;
}

export default class ErrorBoundaryComponent extends Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // store info and allow consumer to react
        this.setState({ error, info });
        if (this.props.onError) {
            try {
                this.props.onError(error, info);
            } catch {
                // swallow errors from handler
            }
        }
        // default logging
        // eslint-disable-next-line no-console
        console.error("Unhandled error in subtree:", error, info);
    }

    renderFallback() {
        const { fallback } = this.props;
        const { error, info } = this.state;

        if (!fallback) {
            return <Container maxWidth="sm">
                <Alert severity="error">
                    <Text variant="h6" component="span">Something went wrong.</Text>
                    {error && (
                        <details style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>
                            <summary>Details</summary>
                            {error?.message}
                            {info?.componentStack && (
                                <>
                                    <hr />
                                    <pre>{info.componentStack}</pre>
                                </>
                            )}
                        </details>
                    )}
                </Alert>
            </Container>;
        }

        if (typeof fallback === "function") {
            return fallback(error as Error, info);
        }

        return fallback;
    }

    render() {
        if (this.state.hasError) {
            return this.renderFallback();
        }
        return this.props.children as ReactNode;
    }
}

export function withErrorBoundary<P extends Record<string, unknown> = Record<string, unknown>>(Component: React.ComponentType<P>, fallback?: ReactNode | FallbackRender) {
    return function (props: P) {
        return (
            <ErrorBoundaryComponent fallback={fallback}>
                <Component {...props} />
            </ErrorBoundaryComponent>
        );
    };
}